/**
 * AgentRailContext — the always-there agent, and the plumbing behind it.
 *
 * The rail lives above the page switch in App, so a conversation survives
 * navigation: you can ask Rex for creators on one page, follow him to the list
 * he built, and keep talking in the same thread.
 *
 * Threads are per agent. Who answers is decided by the page you are on (see
 * pageAgent.ts) unless you pick someone explicitly, and each message carries a
 * one-line description of where you were standing when you sent it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { staffScopeHeaders } from '@/cowork/staffScope';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import { COWORK_API_URL, isCoworkLive } from '@/cowork/live/coworkClient';
import { parseAppPath, type CurrentPage } from '@/routes';
import { readActiveBrandId } from '@/cowork/useActiveBrand';
import { agentForPage, describePageContext, DEFAULT_AGENT_ID, PAGES_WITH_OWN_AGENT } from './pageAgent';

export interface RailAction {
  path: string;
  label: string;
}

export interface RailMessage {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  /** Live tool labels, newest last — shown as a single "working on" line. */
  progress?: string[];
  /** In-app destinations the agent offered this turn. */
  actions?: RailAction[];
  /** Work handed to another agent; links to the task board. */
  tasks?: Array<{ taskId: string; title: string; agentName?: string }>;
  error?: boolean;
}

interface AgentRailValue {
  available: boolean;
  /** Campaign the user currently has open, if any. */
  campaignId?: string;
  /** Page the user is on — drives which openers the rail offers. */
  page: CurrentPage;
  open: boolean;
  setOpen: (v: boolean) => void;
  /** Agent answering right now — page default unless the user picked one. */
  agentId: string;
  pinnedAgentId: string | null;
  pinAgent: (id: string | null) => void;
  messages: RailMessage[];
  streaming: boolean;
  /** Open the rail and send a message. Used by the home composer too. */
  ask: (text: string) => void;
  clearThread: () => void;
  unread: number;
}

// One context object for the life of the page: when this module hot-reloads, a fresh createContext() would leave
// every already-mounted consumer (AppShell) holding the old one and throw "must be used inside <AgentRailProvider>".
const AgentRailContext: React.Context<AgentRailValue | null> = ((globalThis as any).__beatlyAgentRailContext ??= createContext<AgentRailValue | null>(null));

export function useAgentRail(): AgentRailValue {
  const ctx = useContext(AgentRailContext);
  if (!ctx) throw new Error('useAgentRail must be used inside <AgentRailProvider>');
  return ctx;
}

const OPEN_KEY = 'beatly_agent_rail_open';

export function AgentRailProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const route = useMemo(() => parseAppPath(location.pathname), [location.pathname]);
  const [pinnedAgentId, setPinnedAgentId] = useState<string | null>(null);
  const agentId = pinnedAgentId ?? agentForPage(route.currentPage);

  const [open, setOpenState] = useState<boolean>(
    () => localStorage.getItem(OPEN_KEY) === 'true',
  );
  // Threads survive a reload: losing your conversation to F5 made the rail
  // feel like a toy. Persisted per browser, capped to the last 30 messages
  // per agent; streaming flags are stripped on save so a reload never shows
  // a message stuck "streaming".
  const [threads, setThreads] = useState<Record<string, RailMessage[]>>(() => {
    try {
      const raw = localStorage.getItem('cowork_rail_threads_v1');
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, RailMessage[]>;
      for (const k of Object.keys(parsed)) {
        parsed[k] = parsed[k].slice(-30).map(m => ({ ...m, streaming: false }));
      }
      return parsed;
    } catch { return {}; }
  });
  const [sessionIds, setSessionIds] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem('cowork_rail_sessions_v1') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    try {
      const capped: Record<string, RailMessage[]> = {};
      for (const k of Object.keys(threads)) {
        capped[k] = threads[k].slice(-30).map(m => ({ ...m, streaming: false }));
      }
      localStorage.setItem('cowork_rail_threads_v1', JSON.stringify(capped));
    } catch { /* quota — drop persistence silently */ }
  }, [threads]);
  useEffect(() => {
    try { localStorage.setItem('cowork_rail_sessions_v1', JSON.stringify(sessionIds)); }
    catch { /* ignore */ }
  }, [sessionIds]);
  const [streaming, setStreaming] = useState(false);
  const [unread, setUnread] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const setOpen = useCallback((v: boolean) => {
    setOpenState(v);
    localStorage.setItem(OPEN_KEY, String(v));
    if (v) setUnread(0);
  }, []);

  // Abort an in-flight stream if the rail unmounts (logout / hard nav).
  useEffect(() => () => abortRef.current?.abort(), []);

  const messages = threads[agentId] ?? [];

  const pushMessage = useCallback((id: string, msg: RailMessage) => {
    setThreads((prev) => ({ ...prev, [id]: [...(prev[id] ?? []), msg] }));
  }, []);

  const patchLast = useCallback((id: string, patch: (m: RailMessage) => RailMessage) => {
    setThreads((prev) => {
      const thread = prev[id] ?? [];
      if (thread.length === 0) return prev;
      const next = [...thread];
      next[next.length - 1] = patch(next[next.length - 1]);
      return { ...prev, [id]: next };
    });
  }, []);

  const send = useCallback(
    async (text: string, targetAgentId: string) => {
      const history = (threads[targetAgentId] ?? [])
        .filter((m) => !m.error)
        .map((m) => ({ role: m.role, content: m.content }));

      pushMessage(targetAgentId, { role: 'user', content: text });
      pushMessage(targetAgentId, { role: 'assistant', content: '', streaming: true, progress: [] });
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = getTokenFromStorage();
        const res = await fetch(`${COWORK_API_URL}/v1/agents/${targetAgentId}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...staffScopeHeaders(),
          },
          body: JSON.stringify({
            message: text,
            history,
            pageContext: describePageContext({
              page: route.currentPage,
              pathname: location.pathname,
              campaignId: route.campaignId,
              listId: route.listId,
              creatorId: route.creatorId,
            }),
            // Without this the backend falls back to the 'default' brand, so the
            // rail would answer with no Brand OS behind it.
            ...(readActiveBrandId() ? { brandId: readActiveBrandId() } : {}),
            // The campaign on screen — the agent gets its live state rather than
            // having to rediscover stage and gates with tool calls.
            ...(route.campaignId ? { campaignId: route.campaignId } : {}),
            ...(sessionIds[targetAgentId] ? { chatSessionId: sessionIds[targetAgentId] } : {}),
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) throw new Error(`Request failed: ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';
        /** Platform data may have changed under the page — refetch once at the end. */
        let mutatedPlatform = false;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            let evt: Record<string, unknown>;
            try {
              evt = JSON.parse(line.slice(6));
            } catch {
              continue;
            }

            switch (evt.event) {
              case 'text_delta':
                accumulated += String(evt.text ?? '');
                patchLast(targetAgentId, (m) => ({ ...m, content: accumulated }));
                break;

              case 'tool_progress': {
                const label = String(evt.label ?? evt.tool ?? '');
                if (!label) break;
                mutatedPlatform = true;
                patchLast(targetAgentId, (m) => ({
                  ...m,
                  progress: [...(m.progress ?? []).filter((p) => p !== label), label].slice(-4),
                }));
                break;
              }

              case 'ui_navigate':
                patchLast(targetAgentId, (m) => ({
                  ...m,
                  actions: [
                    ...(m.actions ?? []),
                    { path: String(evt.path), label: String(evt.label ?? 'Open') },
                  ],
                }));
                break;

              case 'dashboard_created':
                // A report was built (Kai's create_dashboard). Surface it as a
                // button into Agents › Reports — the rail never navigates on
                // its own.
                patchLast(targetAgentId, (m) => ({
                  ...m,
                  actions: [
                    ...(m.actions ?? []),
                    { path: '/agents/dashboards', label: `Open report: ${String(evt.title ?? 'Dashboard')}` },
                  ],
                }));
                break;

              case 'task_created':
                patchLast(targetAgentId, (m) => ({
                  ...m,
                  tasks: [
                    ...(m.tasks ?? []),
                    {
                      taskId: String(evt.taskId),
                      title: String(evt.title ?? 'Task'),
                      agentName: evt.agentName ? String(evt.agentName) : undefined,
                    },
                  ],
                }));
                break;

              case 'done':
                if (typeof evt.chatSessionId === 'string') {
                  const sid = evt.chatSessionId;
                  setSessionIds((prev) => ({ ...prev, [targetAgentId]: sid }));
                }
                break;

              case 'error':
                throw new Error(String(evt.message ?? 'Agent error'));

              default:
                break;
            }
          }
        }

        patchLast(targetAgentId, (m) => ({ ...m, content: accumulated, streaming: false }));
        // The agent may have created or changed platform objects; let the page
        // behind the rail pick that up rather than showing stale data.
        if (mutatedPlatform) queryClient.invalidateQueries();
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        // Raw provider errors are JSON blobs ("overloaded_error", request ids)
        // that read as a broken product. Translate the known transient ones;
        // keep the real message only for genuinely unknown failures.
        const raw = (err as Error).message || '';
        const friendly = /overloaded|529/i.test(raw)
          ? 'The AI service is briefly over capacity — give it a few seconds and send that again.'
          : /rate.?limit|429/i.test(raw)
            ? 'Hit a rate limit — wait a moment and try again.'
            : /timeout|network|fetch/i.test(raw)
              ? 'Lost the connection mid-reply — check the backend is running and try again.'
              : `Couldn't reach the agent — ${raw}`;
        patchLast(targetAgentId, (m) => ({
          ...m,
          streaming: false,
          error: true,
          content: m.content || friendly,
        }));
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [threads, sessionIds, route, location.pathname, pushMessage, patchLast, queryClient],
  );

  const ask = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;
      setOpen(true);
      void send(trimmed, agentId);
    },
    [agentId, send, setOpen, streaming],
  );

  const clearThread = useCallback(() => {
    setThreads((prev) => ({ ...prev, [agentId]: [] }));
    setSessionIds((prev) => {
      const next = { ...prev };
      delete next[agentId];
      return next;
    });
  }, [agentId]);

  const pinAgent = useCallback((id: string | null) => setPinnedAgentId(id), []);

  // Navigation requested by an agent action button.
  useEffect(() => {
    const handler = (e: Event) => {
      const path = (e as CustomEvent<string>).detail;
      if (typeof path === 'string' && path.startsWith('/')) navigate(path);
    };
    window.addEventListener('agent-rail-navigate', handler);
    return () => window.removeEventListener('agent-rail-navigate', handler);
  }, [navigate]);

  const value = useMemo<AgentRailValue>(
    () => ({
      available: isCoworkLive() && !PAGES_WITH_OWN_AGENT.has(String(route.currentPage)),
      campaignId: route.campaignId,
      page: route.currentPage,
      open,
      setOpen,
      agentId,
      pinnedAgentId,
      pinAgent,
      messages,
      streaming,
      ask,
      clearThread,
      unread,
    }),
    [open, setOpen, agentId, pinnedAgentId, pinAgent, messages, streaming, ask, clearThread, unread, route.campaignId, route.currentPage],
  );

  return <AgentRailContext.Provider value={value}>{children}</AgentRailContext.Provider>;
}

export { DEFAULT_AGENT_ID };
